import { useEffect, useState } from "react"
import { FaRegCircleUser } from "react-icons/fa6"
import { useNavigate } from "react-router-dom";
import { HStack, Menu, Portal, Separator, Spacer, Stack } from "@chakra-ui/react"
import { ALL_STEPS } from "@/constants"
import { useAuth, useViewer } from "@/contexts"
import { useLogout } from "@/hooks"
import { Deal, PortalRole, StepDetail } from "@/types"
import { WhatsNextModal } from "../modals"
import { Button, PulsingDot, Text } from "../ui"

interface Props {
  deals: Deal[]
  isImpersonating?: boolean
}

export const NavBar = ({ deals, isImpersonating }: Props) => {
  const { user } = useAuth()
  const { stopImpersonation } = useViewer()
  const navigate = useNavigate()
  const [isWhatsNextOpen, setIsWhatsNextOpen] = useState(false);
  const [currentStepDetail, setCurrentStepDetail] = useState<StepDetail | null>(null);
  const logoutMutation = useLogout();

  useEffect(() => {
    if (deals && deals.length > 0) {
      const currentDeal = deals[0]; // Assuming you want to track the first deal
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.portalStage);
      setCurrentStepDetail(index !== -1 ? ALL_STEPS[index] : null);
    }
  }, [deals]);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <HStack pt={2} pb={2} pr={5} bg="nav.bg" w="100%" borderBottom={"1px solid"} borderColor="border.default">
      <Stack gap={0} mx={5} cursor="pointer" onClick={() => setIsWhatsNextOpen(true)}>
        <HStack>
          <Text fontSize="md" fontWeight="bold">{currentStepDetail ? currentStepDetail.title : "No Active Step"}</Text>
          <PulsingDot />
        </HStack>
        <Text fontSize="xs" color="text.muted" lineClamp={1}>{currentStepDetail ? currentStepDetail.description : "You have no active steps at the moment."}</Text>
      </Stack>
      <Spacer />
      {
        deals && deals.length > 0 && (
          <Button variant="solid" mr={5} onClick={() => setIsWhatsNextOpen(true)}>
            What's Next?
          </Button>
        )
      }
      {isImpersonating ? (
        <HStack
          bg="orange.500"
          px={3}
          py={1}
          borderRadius="md"
          cursor="pointer"
          _hover={{ bg: "orange.600" }}
          onClick={() => {
            const returnPath = stopImpersonation()
            navigate(returnPath || "/deal-manager/pipeline")
          }}
          role="group"
        >
          <Text fontSize="xs" fontWeight="bold" color="white" _groupHover={{ display: "none" }}>
            Viewing as Client
          </Text>
        </HStack>
      ): (
        <Menu.Root>
          <Menu.Trigger asChild>
            <FaRegCircleUser size={24} cursor="pointer" />
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content bg="darkBlue.200" w="250px">
                <Text color="white" p={2} fontWeight="bold">
                  {user?.userType === PortalRole.CLIENT
                    ? `${user.firstName} ${user.lastName}`
                    : user?.name ?? ''}
                </Text>
                <Separator mb={2} />
                <Menu.Item
                  color="white"
                  fontFamily="body"
                  value="taxes"
                  cursor="pointer"
                  onClick={() => navigate("/client/taxes")}
                >
                  Calculate Your Taxes
                </Menu.Item>
                <Menu.Item
                  color="white"
                  fontFamily="body"
                  value="resources"
                  cursor="pointer"
                  onClick={() => navigate("/client/resources")}
                >
                  Resources
                </Menu.Item>
                <Menu.Item
                  color="white"
                  fontFamily="body"
                  value="newsletter"
                  cursor="pointer"
                  onClick={() => window.open("https://esofund.beehiiv.com", "_blank")}
                >
                  Newsletter
                </Menu.Item>
                <Menu.Item
                  color="white"
                  fontFamily="body"
                  value="privacy-policy"
                  cursor="pointer"
                  onClick={() => window.open("https://www.esofund.com/privacy-policy", "_blank")}
                >
                  Privacy Policy
                </Menu.Item>
                <Menu.Item
                  color="white"
                  fontFamily="body"
                  value="terms"
                  mb={5}
                  cursor="pointer"
                  onClick={() => window.open("https://www.esofund.com/terms-of-use", "_blank")}
                >
                  Terms
                </Menu.Item>
                <Button variant="solid" m={2} color="white" onClick={handleLogout}>
                  Sign Out
                </Button>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      )}
      <WhatsNextModal deals={deals} isOpen={isWhatsNextOpen} onClose={() => setIsWhatsNextOpen(false)} />
    </HStack>
  )
}
